import React from "react";
import { ArrowRight } from "lucide-react";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { SITE } from "../lib/site";

/* ---------- Voltage primary CTA ---------- */
export function VoltageButton({
  as: Tag = "button",
  children,
  className = "",
  withArrow = false,
  testId,
  ...rest
}) {
  return (
    <Tag
      data-testid={testId}
      className={`voltage-cta inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-[15px] font-medium ${className}`}
      {...rest}
    >
      <span>{children}</span>
      {withArrow && <ArrowRight size={18} strokeWidth={2} />}
    </Tag>
  );
}

/* ---------- Outline button (transparent on dark) ---------- */
export function OutlineButton({
  as: Tag = "button",
  children,
  className = "",
  withArrow = false,
  testId,
  ...rest
}) {
  return (
    <Tag
      data-testid={testId}
      className={`group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-[15px] font-medium border border-white/25 text-paper hover:border-voltage hover:text-voltage transition-all duration-300 ${className}`}
      {...rest}
    >
      <span>{children}</span>
      {withArrow && <ArrowRight size={18} strokeWidth={2} className="group-hover:translate-x-1 transition-transform duration-300" />}
    </Tag>
  );
}

/* ---------- Filled ink button (on light bg) ---------- */
export function InkButton({
  as: Tag = "button",
  children,
  className = "",
  withArrow = false,
  testId,
  ...rest
}) {
  return (
    <Tag
      data-testid={testId}
      className={`group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-[15px] font-medium bg-ink text-paper hover:bg-ink-800 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-8px_rgba(37,99,235,0.4)] ${className}`}
      {...rest}
    >
      <span>{children}</span>
      {withArrow && <ArrowRight size={18} strokeWidth={2} className="group-hover:translate-x-1 transition-transform duration-300" />}
    </Tag>
  );
}

/* ---------- Tertiary text link with animated arrow ---------- */
export function TextArrowLink({ children, className = "", testId, onDark = false, as: Tag = "a", ...rest }) {
  return (
    <Tag
      data-testid={testId}
      className={`link-arrow text-[15px] ${onDark ? "text-voltage hover:text-voltage-soft" : "text-electric hover:text-ink-700"} ${className}`}
      {...rest}
    >
      <span className="link-wipe">{children}</span>
      <ArrowRight size={16} strokeWidth={2} className="arrow" />
    </Tag>
  );
}

/* ---------- Pre-built WhatsApp CTAs ---------- */
export function WhatsAppCTA({ label = "Chat on WhatsApp", testId = "whatsapp-cta", className = "" }) {
  return (
    <a
      href={SITE.whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label="Chat on WhatsApp"
      className={`voltage-cta inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md text-[15px] font-medium ${className}`}
    >
      <WhatsAppIcon size={18} strokeWidth={2} />
      <span>{label}</span>
    </a>
  );
}

export function WhatsAppGhost({ label = "Chat on WhatsApp", testId = "whatsapp-ghost", onDark = true, className = "" }) {
  return (
    <a
      href={SITE.whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label="Chat on WhatsApp"
      className={`group inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all duration-300 ${
        onDark
          ? "border-white/20 text-paper hover:border-voltage hover:text-voltage"
          : "border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-paper"
      } ${className}`}
    >
      <WhatsAppIcon size={16} strokeWidth={2} />
      <span>{label}</span>
    </a>
  );
}
